import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, Car, Globe, Music, GraduationCap, Heart, MessageSquare } from 'lucide-react';
import { UserProfile, UserRole } from '@/types';
import { getDashboardPath } from '@/lib/auth';
import { profileApi, messagesApi } from '@/lib/api';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

interface UserProfilePageProps {
  currentUser: UserProfile;
}

const UserProfilePage = ({ currentUser }: UserProfilePageProps) => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [isStartingConversation, setIsStartingConversation] = useState(false);

  const { data: conversations = [] } = useQuery({
    queryKey: ['conversations', currentUser?.id],
    queryFn: messagesApi.getConversations,
    enabled: !!currentUser?.id,
  });

  // Fetch user profile
  const { data: user, isLoading, error } = useQuery({
    queryKey: ['userProfile', userId],
    queryFn: () => profileApi.getProfile(userId!),
    enabled: !!userId,
  });

  // Start conversation mutation
  const startConversationMutation = useMutation({
    mutationFn: ({ userId, rideId }: { userId: string; rideId?: string }) => 
      messagesApi.startConversation({ userId, rideId }),
    onSuccess: (conversation) => {
      // Navigate to the appropriate dashboard based on current user's role
      const targetPath = getDashboardPath(currentUser.role);
      // Pass the conversation ID via query params to open the chat
      navigate(`${targetPath}?openChat=${conversation.id}`);
    },
    onError: () => {
      toast.error('Failed to start conversation');
    },
    onSettled: () => {
      setIsStartingConversation(false);
    },
  });

  const handleMessageUser = () => {
    if (!userId) return;

    const existingConversation = conversations.find((conversation) =>
      conversation.participants.some((participant) => participant.id === userId)
    );

    if (existingConversation) {
      const targetPath = getDashboardPath(currentUser.role);
      navigate(`${targetPath}?openChat=${existingConversation.id}`);
      return;
    }

    setIsStartingConversation(true);
    startConversationMutation.mutate({ userId });
  };

  // Type guard for user name to ensure string type
  const getInitials = (name: string): string => {
    if (!name || typeof name !== 'string') return '?';
    return name
      .split(' ')
      .map(n => n[0])
      .filter(Boolean)
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Safe access to user properties with defaults
  const safeUser: Required<Pick<UserProfile, 'major' | 'languages' | 'favoriteMusic' | 'hobbies' | 'rating' | 'ridesCompleted'>> & 
    Pick<UserProfile, 'name' | 'role'> = {
    name: user?.name ?? 'Unknown User',
    role: (user?.role as UserRole) ?? 'rider',
    major: user?.major ?? 'Undeclared',
    languages: user?.languages ?? ['English'],
    favoriteMusic: user?.favoriteMusic ?? 'Various',
    hobbies: user?.hobbies ?? [],
    rating: user?.rating ?? 5.0,
    ridesCompleted: user?.ridesCompleted ?? 0,
  };

  const getRoleBadgeStyles = (role: UserRole): string => {
    if (role === 'driver') return 'bg-primary/10 text-primary';
    if (role === 'admin' || role === 'super_admin') return 'bg-amber-500/10 text-amber-600';
    return 'bg-accent/10 text-accent';
  };

  const getRoleLabel = (role: UserRole): string => {
    if (role === 'driver') return 'Driver';
    if (role === 'admin') return 'School Admin';
    if (role === 'super_admin') return 'Super Admin';
    return 'Rider';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground animate-pulse">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <p className="text-muted-foreground mb-4">User not found</p>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="container max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-muted transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-display font-bold text-xl">Profile</h1>
        </div>
      </header>

      <main className="container max-w-lg mx-auto px-4 py-5">
        <div className="bg-card rounded-2xl p-6 shadow-warm border border-border/50">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full gradient-warm flex items-center justify-center flex-shrink-0">
                <span className="text-xl font-bold text-primary-foreground">
                  {getInitials(safeUser.name)}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="font-display font-bold text-xl text-foreground truncate">
                    {safeUser.name}
                  </h2>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeStyles(safeUser.role)}`}>
                    {getRoleLabel(safeUser.role)}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <div className="flex items-center gap-1 text-amber-500">
                    <Star className="w-4 h-4 fill-current" />
                    <span className="font-semibold text-sm">{safeUser.rating.toFixed(1)}</span>
                  </div>
                  <span className="text-muted-foreground">•</span>
                  <div className="flex items-center gap-1 text-muted-foreground text-sm">
                    <Car className="w-4 h-4" />
                    <span>{safeUser.ridesCompleted} rides</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Message Button */}
          {user.id !== currentUser.id && (
            <button
              onClick={handleMessageUser}
              disabled={isStartingConversation}
              className="w-full mb-6 flex items-center justify-center gap-2 p-3 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              <MessageSquare className="w-5 h-5" />
              {isStartingConversation ? 'Opening...' : 'Send Message'}
            </button>
          )}

          {/* Intro Section */}
          <div className="space-y-4">
            <h3 className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              About
            </h3>

            <div className="space-y-3">
              {/* Major */}
              <div className="flex items-center gap-3 p-3 bg-muted rounded-xl">
                <div className="p-2 rounded-lg bg-secondary flex-shrink-0">
                  <GraduationCap className="w-4 h-4 text-accent" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-muted-foreground">Major</p>
                  <p className="font-medium text-foreground truncate">{safeUser.major}</p>
                </div>
              </div>

              {/* Languages */}
              <div className="flex items-center gap-3 p-3 bg-muted rounded-xl">
                <div className="p-2 rounded-lg bg-secondary flex-shrink-0">
                  <Globe className="w-4 h-4 text-accent" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-muted-foreground">Languages</p>
                  <p className="font-medium text-foreground truncate">
                    {safeUser.languages.join(', ')}
                  </p>
                </div>
              </div>

              {/* Favorite Music */}
              <div className="flex items-center gap-3 p-3 bg-muted rounded-xl">
                <div className="p-2 rounded-lg bg-secondary flex-shrink-0">
                  <Music className="w-4 h-4 text-accent" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-muted-foreground">Favorite Music</p>
                  <p className="font-medium text-foreground truncate">{safeUser.favoriteMusic}</p>
                </div>
              </div>

              {/* Hobbies */}
              {safeUser.hobbies.length > 0 && (
                <div className="flex items-start gap-3 p-3 bg-muted rounded-xl">
                  <div className="p-2 rounded-lg bg-secondary flex-shrink-0">
                    <Heart className="w-4 h-4 text-accent" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-muted-foreground">Hobbies</p>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {safeUser.hobbies.map((hobby, index) => (
                        <span
                          key={`${hobby}-${index}`}
                          className="px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs font-medium"
                        >
                          {hobby}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserProfilePage;
